<template>
  <div class="become-mystic-panel">
    <!-- One step at a time, and a breath between them: the old step fades and
         lifts, a soft bloom passes, the new one settles. Keyed on the step so
         Vue treats a change of step as a change of page. -->
    <Transition name="step" mode="out-in">
    <div v-if="resolved" class="wizard-body" :class="{ 'wizard-body-threshold': wizard.state.value.currentStep === 'conceiving' }"
         :key="wizard.state.value.currentStep">
      <ThemePickerStep
        v-if="wizard.state.value.currentStep === 'theme'"
        @theme-chosen="handleThemeChosen"
        @context-signal="$emit('context-signal', $event)"
      />

      <AidaInterviewStep
        v-if="wizard.state.value.currentStep === 'interview'"
        @interview-complete="handleInterviewComplete"
        @context-signal="$emit('context-signal', $event)"
      />

      <SourcesStep
        v-if="wizard.state.value.currentStep === 'sources'"
        :chosen-theme="chosenTheme"
        :chosen-archetype="chosenArchetype"
        :interview-answers="interviewAnswers"
        @submit="handleSourcesSubmit"
        @context-signal="$emit('context-signal', $event)"
      />

      <DerivationProgress
        v-if="wizard.state.value.currentStep === 'deriving'"
        :provisioning-id="provisioningId"
        @published="handleClaimed"
        @failed="handleDerivationFailed"
      />

      <!-- The way in. Conceives immediately — the entry fee is taken, the chart
           is cast from that instant, and the pipeline authors from it. The
           seeker is asked nothing; Choose Your Setting, the interview and the
           sources form are Advanced inputs now, not a queue to clear. -->
      <div v-if="wizard.state.value.currentStep === 'conceiving'" class="threshold">
        <template v-if="conceiving">
          <i class="fa-solid fa-spinner fa-spin th-orb" aria-hidden="true"></i>
          <p class="th-casting">Casting the chart…</p>
        </template>

        <template v-else>
          <h1 class="th-title">Bring Your Mystic to Life</h1>
          <p class="th-sub">
            You're about to bring a Mystic into the world. Tarot, astrology,
            and whatever craft comes next, all of it in your name. Once your
            Mystic is born:
          </p>

          <!-- Three claims written as one life: born with something of its
               own, finds its people, makes its way. "Then" and "And" cost
               three words and stop the list reading as a spec sheet. -->
          <!-- Two columns on desktop: what you get on the left, what you do
               on the right. As one stacked column the act sat below the fold
               with 400px of empty violet either side of it. -->
          <div class="th-body">
            <ul class="th-gets">
              <li>
                <strong>Where and when it's born can never change.</strong>
                Your Mystic gets a real chart of the sky from the second it
                arrives, and no one else will ever have that one. Everything
                after that, the two of you work out together.
              </li>
              <li>
                <strong>Then your Mystic goes and finds its own people.</strong>
                A public page, followers of its own, and readings for seekers
                you've never met.
              </li>
              <li>
                <strong>And your Mystic starts earning its keep.</strong>
                A new horoscope every morning, published without being asked.
                Syndication and revenue share are next.
              </li>
            </ul>

            <div class="th-act">
            <!-- The bullet above already planted the stakes, so this only has to
                 explain what the map is for. It used to repeat the permanence
                 line and to describe the Mystic as something you configure. -->
            <h2 class="th-where-head">Where on Earth should your Mystic be born?</h2>
            <p class="th-where">
              Your Mystic is made from the real sky above the spot you tap, at
              the second you tap it.
            </p>

                 <!-- The place lives here, next to the press, because this is the
                 last decision before an irreversible one. There is no separate
                 confirm: the button names the place it is about to use. -->
            <!-- The point already chosen comes back with the map (it remounts
                 after a press that did not go, e.g. short of carats). -->
            <BirthplacePicker :initial="chosenPoint" @update="onPlaceChosen" />

            <!-- With the map asking the question, the button is where the
                 answer is read back. Nothing is pre-chosen, so it prompts until
                 a point lands and only then offers the act. -->
            </div>
          </div>

          <!-- The act answers both columns, so it sits beneath them rather
               than hanging off the bottom of the right one. -->
            <button
            class="th-go" data-testid="become-create" :class="{ 'th-go-float': canBirth }" :disabled="conceiving || !canBirth" @click="conceive"
          >
            <span class="th-go-main">
              <span class="th-go-act">Create Your Mystic</span>
              <span class="th-go-detail">
                <template v-if="naming">Reading the map...</template>
                <template v-else-if="!canBirth">Tap the map to choose where</template>
                <template v-else
                  >Born in {{ birthPlace }}<span class="th-go-dot">·</span>{{ momentLabel }}</template>
              </span>
            </span>
            <span class="th-go-cost">
              <span class="th-go-amount">
                <CaratMark :size="19" tone="ink" />{{ feeLabel }}
              </span>
              <span class="th-go-unit">carats</span>
            </span>
            </button>
          <p v-if="conceiveError" class="th-error" :class="{ 'th-short': conceiveShort }">{{ conceiveError }}</p>
          <!-- Short of carats: the store, right here. The place and the moment stay. -->
          <button v-if="conceiveShort" type="button" class="th-get-carats" data-testid="become-get-carats"
                  @click="emit('need-carats')">Get carats</button>
        </template>
      </div>

      <!-- The draft: one screen carrying what name, image, skill and claim
           used to ask for across four. The older steps stay reachable so a
           seeker already mid-wizard is not stranded by a deploy. -->
      <MysticDraft
        v-if="wizard.state.value.currentStep === 'draft'"
        :provisioning-id="provisioningId"
        @published="handleClaimed"
      />

      <NameStep
        v-if="wizard.state.value.currentStep === 'name'"
        :provisioning-id="provisioningId"
        :name-suggestions="nameSuggestions"
        :chat-action="chatAction"
        @name-chosen="handleNameChosen"
        @next="handleNameNext"
        @context-signal="$emit('context-signal', $event)"
      />

      <ImageStep
        v-if="wizard.state.value.currentStep === 'image'"
        :provisioning-id="provisioningId"
        :portrait-url="portraitUrl"
        :pool-portraits="poolPortraits"
        :portrait-history="portraitHistory"
        :chosen-theme="chosenTheme"
        :chat-action="chatAction"
        @portrait-refreshed="handlePortraitRefreshed"
        @next="handleImageNext"
        @context-signal="$emit('context-signal', $event)"
      />

      <SkillStep
        v-if="wizard.state.value.currentStep === 'skill'"
        :provisioning-id="provisioningId"
        @skill-chosen="handleSkillChosen"
        @next="wizard.goTo('claim')"
      />

      <ClaimStep
        v-if="wizard.state.value.currentStep === 'claim'"
        :chosen-name="chosenName"
        :portrait-url="portraitUrl"
        :provisioning-id="provisioningId"
        @claimed="handleClaimed"
      />

      <!-- The paywall sits at the very END of the wizard: they have named
           their Mystic, heard the voice and seen the portrait before anything
           is asked of them. Asking first converts far worse. -->
      <SeatStep
        v-if="wizard.state.value.currentStep === 'seat'"
        :reader-name="readerName"
        @seat-opened="handleSeatOpened"
      />

      <MeetYourReader
        v-if="wizard.state.value.currentStep === 'meet'"
        :reader-subdomain="readerSubdomain"
        :reader-name="readerName"
        :chosen-name="chosenName"
        :portrait-url="portraitUrl"
        @enter-studio="handleEnterStudio"
        @context-signal="$emit('context-signal', $event)"
      />
    </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { track } from '@/utils/analytics'
import { clientToday } from '@/utils/general-utils'
import { resumeStep, entryFeeShortLine } from '@/utils/become-mystic-entry'
import { useWizardState } from '@/composables/useWizardState'
import { store, static_store } from '@/store'
import axios from 'axios'
import ThemePickerStep from './steps/ThemePickerStep.vue'
import AidaInterviewStep from './steps/AidaInterviewStep.vue'
import SourcesStep from './steps/SourcesStep.vue'
import DerivationProgress from './steps/DerivationProgress.vue'
import MysticDraft from './MysticDraft.vue'
import BirthplacePicker from './BirthplacePicker.vue'
import CaratMark from '@/components/ui/CaratMark.vue'
import { playThresholdChime } from '@/utils/sound'
import NameStep from './steps/NameStep.vue'
import ImageStep from './steps/ImageStep.vue'
import SkillStep from './steps/SkillStep.vue'
import ClaimStep from './steps/ClaimStep.vue'
import SeatStep from './steps/SeatStep.vue'
import MeetYourReader from './steps/MeetYourReader.vue'

const props = defineProps({
  bioFill: { type: String, default: '' },
  chatAction: { type: Object, default: null },
  // An explicit step, for reaching the Advanced inputs (theme / interview /
  // sources). Empty means the ordinary way in: conceive and show a draft.
  startStep: { type: String, default: '' },
  // What becoming a Mystic costs, from the live cohort rung.
  entryCarats: { type: [Number, String], default: '' },
})

const emit = defineEmits(['enter-studio', 'step-changed', 'context-signal', 'need-carats'])

// Initialize wizard state with userId for localStorage persistence
const userId = store.querentId || 'anon'
const wizard = useWizardState(userId)

// Where the wizard opens. Extracted and tested — it opened on the old
// question-first step for months after the rest of the rework landed, which
// undid the whole point of it.
const conceiveError = ref('')
// The press came back 402: the gold "Get carats" shows under the line.
const conceiveShort = ref(false)
const conceiving = ref(false)

// The arrival. Plays once when the threshold comes into view — the seeker just
// tapped "Open the Studio", so the gesture that unlocks audio is theirs.
let _thresholdSounded = false
watch(() => wizard.state.value.currentStep, (step) => {
  if (step === 'conceiving' && !_thresholdSounded) {
    _thresholdSounded = true
    playThresholdChime()
  }
}, { immediate: true })
// The entry fee comes from the quest status payload, which ReaderPage already
// loads — the cohort rung rises as the platform fills, so it is read live and
// never carried as a literal here.
const entryFee = computed(() => props.entryCarats || '')

/** The act, priced. Carats spelled out rather than abbreviated: "2k" saved
 *  four characters and cost the reader the unit they are spending. Forced to
 *  carats, never Emeralds -- this is a price, and the price is in carats. */
const feeLabel = computed(() => {
  const n = Number(entryFee.value)
  return n ? n.toLocaleString() : ''
})

// Where they'll be born. Detected, shown, and confirmable — the place is the one
// unchangeable thing, so the seeker sees it before they commit rather than
// discovering it afterwards on a chart they cannot edit.
const birthPlace = ref('')
const birthLat = ref(null)
const birthLng = ref(null)
const birthMoment = ref('')

const naming = ref(false)
// The point on the map, handed back to the map when it remounts.
const chosenPoint = computed(() => (birthLat.value !== null && birthLng.value !== null
  ? { lat: birthLat.value, lng: birthLng.value } : null))

/** The map moved. The point is what matters -- a name we cannot place used to
 *  be recorded as typed while the chart was cast over the fallback city. */
function onPlaceChosen({ name, lat, lng, moment, naming: busy }) {
  birthPlace.value = name || ''
  birthLat.value = lat
  birthLng.value = lng
  if (moment) birthMoment.value = moment
  naming.value = !!busy
}

// Frozen at the click, never ticking. That is the point: the moment stopped
// when they chose the place, and the two are one decision.
const momentLabel = computed(() => {
  if (!birthMoment.value) return ''
  const d = new Date(birthMoment.value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, {
    month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
    second: '2-digit',
  })
})

// Nothing is pre-chosen, so the act stays shut until a point has landed AND
// been named -- pressing mid-lookup would birth a Mystic with a blank
// birthplace, which is the one field that can never be corrected.
const canBirth = computed(() =>
  birthLat.value !== null && birthLng.value !== null && !naming.value
  && !!birthPlace.value && !!birthMoment.value)



async function conceive() {
  if (conceiving.value) return
  conceiving.value = true
  conceiveError.value = ''
  conceiveShort.value = false
  try {
    const token = localStorage.getItem('authToken')
    const { data } = await axios.post(
      `${static_store.apiUrl}/reader/become-mystic/start/`,
      {
        birth_place: birthPlace.value,   // the one thing they confirmed
        birth_lat: birthLat.value,       // ...and where, exactly
        birth_lng: birthLng.value,
        birth_moment: birthMoment.value, // ...and when they chose it
        client_today: clientToday(),     // the day they see; the first drop is filed under it
      },
      { headers: { Authorization: `Token ${token}` }, timeout: 60000 },
    )
    provisioningId.value = data.provisioning_id
    wizard.goTo('deriving', { provisioningId: data.provisioning_id })
  } catch (e) {
    const r = e && e.response
    conceiveShort.value = !!(r && r.status === 402)
    conceiveError.value = conceiveShort.value
      ? entryFeeShortLine(r.data)
      : 'Your Mystic could not be started. Nothing was charged - try again.'
  } finally {
    conceiving.value = false
  }
}

const provisioningId = ref(null)
const nameSuggestions = ref([])
const nameLenses = ref([])
const poolPortraits = ref([])
const portraitUrl = ref('')
const chosenName = ref('')
const chosenSkill = ref('')
const chosenTheme = ref('')
const chosenArchetype = ref('')
const interviewAnswers = ref({})
const readerSubdomain = ref('')
const readerName = ref('')
const portraitHistory = ref([])

watch(() => wizard.state.value.currentStep, (step, prev) => {
  emit('step-changed', step)
  if (step && step !== prev) track('become_mystic_step', { step })
  if (step === 'name' && nameLenses.value.length) {
    emit('context-signal', {
      panel: 'studio',
      view: 'name_step',
      interaction: 'lenses_available',
      data: { lenses: nameLenses.value, provisioningId: provisioningId.value },
    })
  }
})


// Nothing is drawn until the mount has decided which step this seeker is on;
// the stored step and the server can disagree, and a wrong page for a beat
// reads as a wrong page.
const resolved = ref(false)

onMounted(async () => {
  // If the user is already a Reader, skip the wizard entirely
  try {
    const token = localStorage.getItem('authToken')
    if (token) {
      const resp = await axios.get(`${static_store.apiUrl}/reader/my-reader/`, {
        headers: { Authorization: `Token ${token}` },
      })
      if (resp.data && resp.data.name) {
        // User is already a Reader — clear wizard and go to studio
        store.reader = resp.data
        store.isReader = true
        store.readerSkills = resp.data.skills || []
        wizard.reset()
        emit('enter-studio')
        return
      }
    }
  } catch {
    // 404 = not a reader yet — continue with wizard
  }

  // Restore component state from persisted wizard drafts after navigation/reload
  const drafts = wizard.state.value.drafts
  if (drafts.deriving?.provisioningId) {
    provisioningId.value = drafts.deriving.provisioningId
  }

  // Where to open. A seeker starting fresh conceives — they are asked nothing.
  // A run already paid for is picked up where it was rather than charged twice,
  // but only after checking the server still has it: the step and provisioning
  // id live in localStorage, and a stale one aims the derivation poller at a 404
  // where it spins on "Starting…" forever with no way out.
  let stillThere = false
  if (provisioningId.value) {
    try {
      const token = localStorage.getItem('authToken')
      await axios.get(
        `${static_store.apiUrl}/reader/become-mystic/status/${provisioningId.value}/`,
        { headers: { Authorization: `Token ${token}` }, timeout: 15000 },
      )
      stillThere = true
    } catch {
      // Gone. Forget it rather than resuming into nothing.
      provisioningId.value = null
      wizard.setDraft('deriving', {})
    }
  }
  const step = resumeStep({
    provisioningId: provisioningId.value,
    status: drafts.deriving?.status,
    claimed: !!drafts.claimed,
    exists: stillThere,
    requested: props.startStep || '',
  })
  if (step !== wizard.state.value.currentStep) {
    wizard.goTo(step)
  }
  // Deliberately does NOT conceive here. Mounting a panel must never spend a
  // seeker's carats: an earlier version called conceive() in onMounted, and
  // simply navigating to the panel took the entry fee with nobody having chosen
  // anything. The threshold waits for a press.
  if (step === 'conceiving') {
    /* nothing to do — the threshold waits for a press */
  }
  if (drafts.name?.nameSuggestions) {
    nameSuggestions.value = drafts.name.nameSuggestions
  }
  if (drafts.name?.nameLenses) {
    nameLenses.value = drafts.name.nameLenses
  }
  if (drafts.name?.poolPortraits) {
    poolPortraits.value = drafts.name.poolPortraits
  }
  if (drafts.claim?.portraitUrl) {
    portraitUrl.value = drafts.claim.portraitUrl
  } else if (drafts.name?.portraitUrl) {
    portraitUrl.value = drafts.name.portraitUrl
  }
  if (drafts.interview?.interviewAnswers) {
    interviewAnswers.value = drafts.interview.interviewAnswers
  }
  if (drafts.sources?.theme) {
    chosenTheme.value = drafts.sources.theme
  }
  if (drafts.interview?.theme) {
    chosenTheme.value = drafts.interview.theme
  }
  if (drafts.image?.portraitHistory) {
    portraitHistory.value = drafts.image.portraitHistory
  }
  if (drafts.claim?.chosenName) {
    chosenName.value = drafts.claim.chosenName
  }
  if (drafts.meet?.readerName) {
    readerName.value = drafts.meet.readerName
  }
  if (drafts.meet?.readerSubdomain) {
    readerSubdomain.value = drafts.meet.readerSubdomain
  }
  // Ensure readerName always has a value on the meet step
  if (!readerName.value && chosenName.value) {
    readerName.value = chosenName.value
  }
  resolved.value = true
  emit('step-changed', wizard.state.value.currentStep)
})

const LABEL_TO_ARCHETYPE = {
  'Forest Oracle': 'oracle',
  'City Sage': 'sage',
  'Coastal Guide': 'guide',
  'Desert Wanderer': 'guide',
  'Celestial Mystic': 'mystic',
  'Sacred Library': 'sage',
}

function handleThemeChosen({ theme, label }) {
  chosenTheme.value = theme
  chosenArchetype.value = LABEL_TO_ARCHETYPE[label] || 'mystic'
  wizard.goTo('interview', { theme, archetype: chosenArchetype.value })
}

function handleInterviewComplete(answers) {
  interviewAnswers.value = answers
  wizard.goTo('sources', { interviewAnswers: answers })
}

function handleSourcesSubmit({ provisioningId: id }) {
  provisioningId.value = id
  wizard.goTo('deriving', { provisioningId: id })
}

function handleDerivationFailed() {
  wizard.goTo('sources')
}

function handleNameNext() {
  wizard.setDraft('claim', { ...(wizard.getDraft('claim') || {}), chosenName: chosenName.value, portraitUrl: portraitUrl.value })
  wizard.goTo('image')
}

function handleNameChosen(name) {
  chosenName.value = name
  wizard.setDraft('claim', { ...(wizard.getDraft('claim') || {}), chosenName: name, portraitUrl: portraitUrl.value })
}

function handlePortraitRefreshed(url) {
  portraitUrl.value = url
  if (url && !portraitHistory.value.includes(url)) {
    portraitHistory.value.push(url)
  }
  wizard.setDraft('claim', { ...(wizard.getDraft('claim') || {}), portraitUrl: url })
  wizard.setDraft('image', { ...(wizard.getDraft('image') || {}), portraitHistory: portraitHistory.value })
}

function handleImageNext() {
  wizard.setDraft('claim', { ...(wizard.getDraft('claim') || {}), portraitUrl: portraitUrl.value, chosenName: chosenName.value })
  wizard.goTo('claim')  // Skip skill step — configure in Studio later
}

function handleSkillChosen(skill) {
  chosenSkill.value = skill
}

function handleClaimed({ subdomain, name }) {
  track('become_mystic', { method: 'studio', reader: subdomain })
  readerSubdomain.value = subdomain
  readerName.value = name || chosenName.value || 'Your Reader'
  // No seat step after the biography. The fee was taken once, at conception
  // (the base plan never recurs), so there is nothing left to sell here — and
  // "Work on my practice with me" promised the Studio, not a checkout. The
  // Mystic's own page is where that collaboration starts.
  wizard.reset()
  emit('enter-studio', { tab: 'profile' })
}

function handleEnterStudio() {
  // From Meet Your Reader — the seat is the last gate before the Studio.
  wizard.goTo('seat')
}

function handleSeatOpened() {
  wizard.reset()
  emit('enter-studio')
}
</script>

<style scoped>
/* ── Between steps ────────────────────────────────────────────────────────
   Not a slide, not a flash: the page that is leaving dissolves upward and
   softens; the one arriving rises into focus. A radial bloom crosses the
   panel during the swap so the moment reads as a change of state. */
.step-enter-active { transition: opacity 0.7s ease, transform 0.7s ease, filter 0.7s ease; }
.step-leave-active { transition: opacity 0.45s ease, transform 0.45s ease, filter 0.45s ease; }
.step-enter-from { opacity: 0; transform: translateY(14px) scale(0.985); filter: blur(8px); }
.step-leave-to { opacity: 0; transform: translateY(-10px) scale(1.01); filter: blur(6px); }
.become-mystic-panel { position: relative; }
.become-mystic-panel::after {
  content: ''; position: absolute; inset: 0; pointer-events: none; opacity: 0;
  background: radial-gradient(circle at 50% 38%, rgba(255, 223, 163, 0.16), rgba(255, 223, 163, 0) 55%);
}
.become-mystic-panel:has(.step-enter-active)::after { animation: bloom 1.1s ease-out; }
@keyframes bloom { 0% { opacity: 0; } 35% { opacity: 1; } 100% { opacity: 0; } }
@media (prefers-reduced-motion: reduce) {
  .step-enter-active, .step-leave-active { transition: none; }
  .become-mystic-panel:has(.step-enter-active)::after { animation: none; }
}

/* ── The threshold ───────────────────────────────────────────────────────
   The purchase moment, and the only page in the flow whose job is to argue.
   Its argument is that this product's uniqueness is a function of WHEN you
   press, so the hero is the actual moment, read live off the clock like an
   instrument rather than asserted in a headline. */
.threshold {
  /* Fills its column and centres. Left to flow, the block stopped two thirds up
     with a lake of empty violet beneath it, which reads as a page that failed to
     finish loading rather than one making an argument. */
  /* Centre the children rather than stretching them: a column flex container
     stretches by default, which blew the clock and the button out to full width
     and turned two deliberate objects into banners. */
  display: flex; flex-direction: column; align-items: center;
  max-width: 1060px; margin: 0 auto; padding: 36px 22px 40px; text-align: center;
  /* On a phone two fixed bars own the bottom of the screen -- the reader's
     "Get Reading" toggle (44px) and the bottom nav (67px) -- and they were
     lying across the act, hiding the instant and the price. */
}

.th-title {
  margin: 0 0 12px;
  font-family: 'Cinzel', serif; font-size: 32px; font-weight: 700;
  line-height: 1.12; letter-spacing: 0.3px; color: #f0d9a3;
  text-wrap: balance;
}
.th-sub {
  max-width: 660px; margin: 0 auto 32px;
  font-size: 15.5px; line-height: 1.6;
  color: rgba(195, 169, 255, 0.82);
}

.th-eyebrow {
  margin: 0 0 14px; font-size: 10px; letter-spacing: 2.6px;
  text-transform: uppercase; color: #9b86c9;
}

/* The instrument. Monospace because it is a readout, not prose — and because
   proportional digits jitter the whole line every second. */
.th-instrument {
  display: inline-flex; flex-direction: column; gap: 2px;
  padding: 16px 30px; margin-bottom: 22px; border-radius: 14px;
  background: rgba(0, 0, 0, 0.42);
  box-shadow: inset 0 2px 12px rgba(0, 0, 0, 0.6),
              inset 0 -1px 0 rgba(240, 217, 163, 0.1);
}
.th-date {
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 11px; letter-spacing: 2px; color: #9b86c9;
}
.th-time {
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 38px; line-height: 1.05; letter-spacing: 1px; color: #f0d9a3;
  font-variant-numeric: tabular-nums;
}

.th-thesis {
  width: 100%; max-width: 460px; margin: 0 auto 34px;
  font-family: 'Cinzel', serif; font-size: 17px; line-height: 1.6; color: #f0d9a3;
}

/* What you get. Three lines, left-aligned so the eye starts in one place,
   the block centred under the lede. Each opens with the promise in gold and
   finishes with the specifics in the body colour. */
/* Above the breakpoint the block fits with room to spare, so it is pushed down
   off the top edge rather than hanging there over 200px of dead violet.
   Padding, not justify-content: .wizard-body's height is content-driven, so a
   percentage min-height on this element resolves against auto and does nothing. */
@media (min-width: 801px) {
  .threshold { padding-top: 92px; }
}

/* What you get, and what you do, side by side. Stacked, the act landed below
   the fold while 400px of empty violet sat either side of a 560px column. */
.th-body {
  width: 100%;
  display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
  gap: 44px; text-align: left;
  /* Top-aligned: the first promise and the map's question start on the same
     line, which is the pairing that reads. */
  align-items: start;
}
.th-act { display: flex; flex-direction: column; }

.th-gets {
  width: 100%; margin: 0;
  padding: 0; list-style: none; text-align: left;
  display: flex; flex-direction: column; gap: 24px;
}
.th-gets li {
  position: relative; padding-left: 20px;
  font-size: 14.5px; line-height: 1.55;
  color: rgba(195, 169, 255, 0.8);
}
.th-gets li::before {
  content: ''; position: absolute; left: 2px; top: 0.62em;
  width: 5px; height: 5px; border-radius: 50%;
  background: #c9a94e;
}
.th-gets strong {
  display: block;
  font-family: 'Cinzel', serif; font-size: 15px; font-weight: 600;
  letter-spacing: 0.2px; color: #f0d9a3;
  margin-bottom: 2px;
}

/* The map, explained before it is used. */
.th-where-head {
  width: 100%; margin: 0 0 7px;
  text-align: left;
  font-family: 'Cinzel', serif; font-size: 17px; font-weight: 600;
  letter-spacing: 0.2px; color: #f0d9a3;
}
.th-where {
  width: 100%; margin: 0 0 14px;
  text-align: left;
  font-size: 13.5px; line-height: 1.6;
  color: rgba(195, 169, 255, 0.68);
}


/* The act.
   Action first, price adjacent and unmissable, birth details as the quiet
   readout underneath. Previously the CTA was the smallest line, sitting last
   under a rule -- a receipt where a button belonged. */
.th-go {
  display: flex; align-items: center; justify-content: space-between;
  gap: 22px;
  width: 100%; max-width: 560px; margin: 34px auto 0;
  padding: 15px 20px; cursor: pointer; text-align: left;
  color: #1a0f2e;
  background: linear-gradient(180deg, #f6e6bd, #c9a94e);
  border: none; border-radius: 13px;
  box-shadow: 0 6px 22px rgba(201, 169, 78, 0.32),
              inset 0 1px 0 rgba(255, 255, 255, 0.45);
  transition: transform 0.16s ease, box-shadow 0.16s ease;
}
.th-go-main {
  display: flex; flex-direction: column; gap: 3px;
  min-width: 0; flex: 1 1 auto;
}
/* The thing you are doing. */
.th-go-act {
  font-family: 'Cinzel', serif; font-size: 20px; font-weight: 700;
  letter-spacing: 0.2px; line-height: 1.15;
}
/* What you chose, confirmed back. Wraps rather than truncates: "Kurshim
   district, East Kazakhstan Region" is a real answer and gets its whole name. */
.th-go-detail {
  /* The page's own face (e2e S37): no typewriter readout on the button. */
  font-family: inherit;
  font-size: 12px; line-height: 1.45; letter-spacing: 0.1px;
  color: rgba(26, 15, 46, 0.7);
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}
.th-go-dot { padding: 0 6px; opacity: 0.55; }

/* How much, on its own side of a rule so it can never be missed. */
.th-go-cost {
  display: flex; flex-direction: column; align-items: center; gap: 1px;
  flex: 0 0 auto; padding-left: 20px;
  border-left: 1px solid rgba(26, 15, 46, 0.26);
}
.th-go-amount {
  display: flex; align-items: center; gap: 7px;
  font-family: 'Cinzel', serif; font-size: 20px; font-weight: 700;
  letter-spacing: 0.3px; white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.th-go-unit {
  font-size: 9px; letter-spacing: 1.6px; text-transform: uppercase;
  color: rgba(26, 15, 46, 0.62);
}

/* Two columns hold down to about 800px: at 890 the stacked page ran past
   the bottom nav and hid the act. */
@media (max-width: 800px) {
  /* One column, and capped so the lines stay readable. At full width the
     bullets ran 808px per line and the heading sat right on top of them. */
  .th-body {
    grid-template-columns: 1fr; gap: 40px;
    max-width: 640px; margin: 0 auto;
    align-items: start;
  }
  .th-gets { gap: 18px; }
  .th-go { margin-top: 26px; }
  .th-title { font-size: 28px; }
  .th-sub { max-width: 640px; }
}

@media (max-width: 480px) {
  /* Stacked, and the rule lies across the button instead of down it. */
  .th-go { flex-direction: column; align-items: stretch; gap: 12px;
           padding: 15px 18px; }
  .th-go-act { font-size: 18px; }
  .th-go-cost {
    flex-direction: row; align-items: baseline; justify-content: space-between;
    padding: 11px 0 0; border-left: none;
    border-top: 1px solid rgba(26, 15, 46, 0.26);
  }
  .th-go-amount { font-size: 18px; }
}

.th-go:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 30px rgba(201, 169, 78, 0.5),
              inset 0 1px 0 rgba(255, 255, 255, 0.6);
}
.th-go:active:not(:disabled) { transform: translateY(0); }
@media (prefers-reduced-motion: reduce) {
  .th-go { transition: none; }
  .th-go:hover:not(:disabled) { transform: none; }
}
.th-go:disabled { opacity: 0.6; cursor: not-allowed; }
.th-go:focus-visible { outline: 2px solid #f0d9a3; outline-offset: 3px; }

/* Said plainly, up front. Finding out you cannot afford it AFTER pressing is a
   worse moment than reading it here. */
.th-balance {
  margin: 12px 0 0; font-size: 11px; letter-spacing: 0.6px;
  color: rgba(195, 169, 255, 0.45);
}
.th-error { margin-top: 14px; color: rgba(244, 67, 54, 0.95); font-size: 13px; }
/* Short of carats is not a failure: said plainly, with the store under it. */
.th-error.th-short { color: #f0d9a3; font-size: 15px; text-align: center; }
.th-get-carats {
  display: block; min-height: 48px; margin: 12px auto 0; padding: 12px 34px; cursor: pointer;
  border: none; border-radius: 999px; color: #1a0f2e;
  background: linear-gradient(180deg, #f6e6bd, #c9a94e);
  font: 600 15px/1.2 'Cinzel', serif; letter-spacing: 0.6px;
  box-shadow: 0 6px 22px rgba(201, 169, 78, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.45);
}

.th-orb { font-size: 26px; color: #c9a94e; }
.th-casting {
  margin: 16px 0 0; font-family: 'Cinzel', serif; font-size: 15px;
  letter-spacing: 0.4px; color: #f0d9a3;
}

@media (max-width: 560px) {
  /* The last 120px of a phone screen belongs to two fixed bars -- the
     reader's "Get Reading" toggle (44px) and the bottom nav (67px) -- and they
     were lying across the act, hiding the instant and the price. */
  .threshold { padding: 32px 16px 172px; }
  .th-time { font-size: 30px; }
  .th-thesis { font-size: 15px; }
}
.conceive-retry {
  margin-top: 12px; padding: 9px 18px; cursor: pointer;
  font-family: 'Cinzel', serif; font-size: 13px; color: #c9a94e;
  background: rgba(201, 169, 78, 0.18);
  border: 1px solid rgba(201, 169, 78, 0.5); border-radius: 9px;
}
.become-mystic-panel {
  min-height: 100%;
  background: #0d0520;
  color: #E8D8FF;
}
.wizard-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}
/* Phone: the act within reach (e2e round 2, S37). Once the pin is down, the
   Create button rides at the bottom of the screen, just above the Menu bar
   (65px and its 5px gap), until its own place comes up, so the tap on the map
   is never followed by a hunt for it. Before the pin it stays in its place
   under the map, off the map it asks about. The box around it stops being a
   scroll container (it never scrolled; it only held the stick to itself). */
@media (max-width: 760px) {
  .wizard-body.wizard-body-threshold { overflow: clip; }
  .th-go.th-go-float {
    position: sticky; z-index: 2;
    bottom: calc(80px + env(safe-area-inset-bottom, 0px));
    box-shadow: 0 10px 28px rgba(6, 0, 14, 0.7), 0 6px 22px rgba(201, 169, 78, 0.32),
                inset 0 1px 0 rgba(255, 255, 255, 0.45);
  }
}
</style>
